import { ReactNode, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Dropdown } from '@Components/Dropdown';
import { ApiSentryChange, ApiSentryChangeType } from '@blisspointmedia/bpm-types/dist/ApiSentry';
import { Size } from '@Components/designConstants';
import { getChanges, getChangesMeta } from '../api-sentry-api';

const CHANGE_TYPE_ORDER: ApiSentryChangeType[] = [
  'REMOVAL',
  'BREAKING',
  'DEPRECATION',
  'RENAME',
  'ADDITION'
];

const entityLabel = (c: ApiSentryChange): string =>
  c.entity || (c.resource && c.field ? `${c.resource}.${c.field}` : c.resource || '—');

const ChangesTable = ({ changes }: { changes: ApiSentryChange[] }) => (
  <table className='api-sentry-changes-table'>
    <thead>
      <tr>
        <th>Entity</th>
        <th>Platform</th>
        <th>Type</th>
        <th>Version</th>
        <th>Date</th>
        <th>Details</th>
      </tr>
    </thead>
    <tbody>
      {changes.map(c => (
        <tr key={c.id} className={`api-sentry-row api-sentry-row--${c.changeType.toLowerCase()}`}>
          <td className='api-sentry-entity-cell'>{entityLabel(c)}</td>
          <td>{c.platformName}</td>
          <td>
            <span className={`api-sentry-badge api-sentry-badge--${c.changeType.toLowerCase()}`}>
              {c.changeType}
            </span>
          </td>
          <td>{c.apiVersion || '—'}</td>
          <td>{c.effectiveDate || c.sunsetDate || '—'}</td>
          <td>
            {c.details}
            {c.changeType === 'RENAME' && c.oldName && c.newName && (
              <div className='api-sentry-rename'>
                <span className='api-sentry-rename-old'>{c.oldName}</span>
                {' → '}
                <span className='api-sentry-rename-new'>{c.newName}</span>
              </div>
            )}
            {c.sourceUrl && (
              <div>
                <a href={c.sourceUrl} target='_blank' rel='noreferrer'>
                  View source
                </a>
              </div>
            )}
          </td>
        </tr>
      ))}
    </tbody>
  </table>
);

const renderResults = (
  isLoading: boolean,
  changes: ApiSentryChange[],
  filtered: ApiSentryChange[]
): ReactNode => {
  if (isLoading) {
    return <div className='api-sentry-empty-state'>Loading...</div>;
  }
  if (filtered.length === 0) {
    return (
      <div className='api-sentry-empty-state'>
        {changes.length === 0
          ? 'No data yet — run the pipeline first.'
          : 'No changes match the current filters.'}
      </div>
    );
  }
  return <ChangesTable changes={filtered} />;
};

export const ChangesDashboard = () => {
  const [platformFilter, setPlatformFilter] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const { data: changes = [], isLoading } = useQuery({
    queryKey: ['api-sentry-changes'],
    queryFn: () => getChanges()
  });
  const { data: meta } = useQuery({
    queryKey: ['api-sentry-changes-meta'],
    queryFn: () => getChangesMeta()
  });

  const platformOptions = useMemo(
    () => Array.from(new Set(changes.map(c => c.platformName))).sort(),
    [changes]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return changes.filter(c => {
      if (platformFilter && c.platformName !== platformFilter) {
        return false;
      }
      if (typeFilter && c.changeType !== typeFilter) {
        return false;
      }
      if (q && !`${entityLabel(c)} ${c.details ?? ''}`.toLowerCase().includes(q)) {
        return false;
      }
      return true;
    });
  }, [changes, platformFilter, typeFilter, search]);

  const criticalCount = changes.filter(
    c => c.changeType === 'REMOVAL' || c.changeType === 'BREAKING'
  ).length;
  const additionsCount = changes.filter(c => c.changeType === 'ADDITION').length;

  return (
    <div className='api-sentry-dashboard'>
      <div className='api-sentry-stat-tiles'>
        <div className='api-sentry-stat-tile'>
          <div className='api-sentry-stat-value'>{changes.length}</div>
          <div className='api-sentry-stat-label'>Total Changes</div>
        </div>
        <div className='api-sentry-stat-tile api-sentry-stat-tile--critical'>
          <div className='api-sentry-stat-value'>{criticalCount}</div>
          <div className='api-sentry-stat-label'>Critical (Removals + Breaking)</div>
        </div>
        <div className='api-sentry-stat-tile'>
          <div className='api-sentry-stat-value'>{additionsCount}</div>
          <div className='api-sentry-stat-label'>Additions</div>
        </div>
        {meta?.lastUpdated && (
          <div className='api-sentry-stat-tile'>
            <div className='api-sentry-stat-value'>
              {new Date(meta.lastUpdated).toLocaleDateString()}
            </div>
            <div className='api-sentry-stat-label'>Last Updated</div>
          </div>
        )}
      </div>

      <div className='api-sentry-filter-bar'>
        <Dropdown
          size={Size.LG}
          searchable
          options={platformOptions}
          value={platformFilter}
          onChange={setPlatformFilter}
          placeholder='All platforms'
          menuPortalTarget={document.body}
        />
        <Dropdown
          size={Size.LG}
          searchable
          options={CHANGE_TYPE_ORDER}
          value={typeFilter}
          onChange={setTypeFilter}
          placeholder='All types'
          menuPortalTarget={document.body}
        />
        <input
          className='api-sentry-search-input'
          type='text'
          placeholder='Search entity or details...'
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>

      {renderResults(isLoading, changes, filtered)}
    </div>
  );
};
